/**
 * Studio page entry. Builds the workspace UI, wires all panels into
 * a PanelManager, registers the StudioKokoBridge as the widget's
 * extension surface, and lands the user on the checklist by default.
 *
 * The widget itself (`<koko-companion>`) is mounted by the HTML.
 * We just import `embed.ts` so the custom-element class gets
 * registered before the browser tries to upgrade the element.
 */

import "../embed";
import "./studio.css";

import { ScriptUploadPanel } from "./panels/ScriptUploadPanel";
import { TimelinePanel } from "./panels/TimelinePanel";
import { CharacterCreatorPanel } from "./panels/CharacterCreatorPanel";
import { AssetsPanel } from "./panels/AssetsPanel";
import { MoodBoardPanel } from "./panels/MoodBoardPanel";
import { CostumePanel } from "./panels/CostumePanel";
import { LocationPanel } from "./panels/LocationPanel";
import { StoryboardPanel } from "./panels/StoryboardPanel";
import { FinalVideoPanel } from "./panels/FinalVideoPanel";
import { PANEL_REGISTRY, PanelManager, type StudioPanelId } from "./PanelManager";
import { StudioStore } from "./StudioStore";
import { StudioKokoBridge } from "./StudioKokoBridge";
import { Timeline } from "./Timeline";
import { ScriptDock } from "./ScriptDock";
import { Sidebar } from "./Sidebar";
import { BranchField } from "./branches/BranchField";
import type { KokoElement } from "../KokoElement";
import {
  applyKokoInteractionModeToDocument,
  getKokoInteractionMode,
  subscribeKokoInteractionMode,
  type KokoInteractionMode,
} from "./KokoInteractionMode";
import { mountSharedEditDrawer } from "./shared";

type StudioTheme = "light" | "dark";

const THEME_STORAGE_KEY = "koko-studio-theme";

type StudioBackground = "paper" | "mountains" | "meadow" | "clouds";

const BACKGROUND_STORAGE_KEY = "koko-studio-background";

const BACKGROUND_OPTIONS: ReadonlyArray<{
  id: StudioBackground;
  label: string;
  thumb: string | null;
}> = [
  { id: "paper", label: "Paper", thumb: null },
  { id: "meadow", label: "Meadow", thumb: "/backgrounds/meadow-thumb.jpg" },
  { id: "clouds", label: "Clouds", thumb: "/backgrounds/clouds-thumb.jpg" },
];

// Run after the DOM is parsed so `#studio-root` and `<koko-companion>`
// exist and the inline window.__KOKO_CONFIG__ has been set.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootStudio, { once: true });
} else {
  bootStudio();
}

function bootStudio(): void {
  const studioRoot = document.getElementById("studio-root");
  if (!studioRoot) {
    console.error("[studio] #studio-root not found in DOM — cannot boot.");
    return;
  }
  const initialTheme = readPreferredTheme();
  applyTheme(initialTheme);
  const initialBackground = readPreferredBackground();
  applyBackground(initialBackground);
  installBackgroundLayer();
  applyKokoInteractionModeToDocument();
  installHoverColdGuard();

  // Core controllers.
  const studioStore = new StudioStore();
  // Worker base URL for studio-side network calls (e.g. per-line TTS in
  // the script editor). Same source the widget uses — dev config global,
  // falling back to the <koko-companion api="…"> attribute.
  const workerBaseUrl =
    window.__KOKO_CONFIG__?.api ??
    document.getElementById("koko")?.getAttribute("api") ??
    "";

  // Build the page chrome (top strip + per-panel header host) once.
  const layout = buildPageChrome(initialTheme);
  layout.themeToggleButton.addEventListener("click", () => {
    const nextTheme = getActiveTheme() === "dark" ? "light" : "dark";
    applyTheme(nextTheme);
    syncThemeToggleLabel(layout.themeToggleButton, nextTheme);
  });
  wireBackgroundPicker(layout.backgroundPicker, initialBackground);
  subscribeKokoInteractionMode((mode) => {
    syncFooterHint(layout.footerHint, mode);
  });

  // Sidebar drives navigation; its toggles need the dock + timeline, and
  // those need the sidebar back to keep their toggle state in sync — so
  // `sidebar` is captured lazily by the state-change callbacks below.
  let sidebar: Sidebar | null = null;

  // Persistent footer-docked timeline. Lives outside the PanelManager so
  // it survives every panel switch. Mounted on <body> (not the shell)
  // because the shell keeps a `transform` from its enter animation, which
  // would otherwise become the containing block for this fixed dock.
  const timeline = new Timeline({
    store: studioStore,
    onStateChange: (expanded) => sidebar?.setTimelineExpanded(expanded),
  });
  document.body.appendChild(timeline.getElement());
  timeline.start();

  const panelManager = new PanelManager(layout.panelHost);
  // Test/demo hook: lets the studio-tour recorder drive panel navigation.
  window.__studioPanelManager = panelManager;
  // Centralized store handle for tests/demos and cross-panel wiring
  // (panels used to each expose this defensively; integrator owns it now).
  window.__studioStore = studioStore;
  panelManager.subscribe((nextPanelId) => {
    layout.headerHost.replaceChildren(buildHeader(nextPanelId));
  });

  // The screenplay is now a persistent, toggleable column rather than a
  // swappable panel. The manager delegates `open("script_editor")` to it
  // so directives, the upload panel's "start writing", and timeline jumps
  // all keep working unchanged.
  const scriptDock = new ScriptDock({
    store: studioStore,
    workerBaseUrl,
    panelManager,
    onStateChange: (open) => sidebar?.setScriptOpen(open),
  });
  panelManager.registerExternalOpener("script_editor", () =>
    scriptDock.setOpen(true),
  );

  // Assemble the workspace row: sidebar → script column → main area
  // (per-panel header + panel host + footer hint).
  sidebar = new Sidebar({
    store: studioStore,
    panelManager,
    scriptDock,
    timeline,
  });

  const mainColumn = document.createElement("div");
  mainColumn.className = "studio-main";
  mainColumn.appendChild(layout.headerHost);
  mainColumn.appendChild(layout.panelHost);
  mainColumn.appendChild(layout.footerHint);

  const workspace = document.createElement("div");
  workspace.className = "studio-workspace";
  workspace.appendChild(sidebar.getElement());
  workspace.appendChild(scriptDock.getElement());
  workspace.appendChild(mainColumn);

  studioRoot.appendChild(layout.topStrip);
  studioRoot.appendChild(workspace);

  sidebar.start();

  studioStore.subscribeProjectTitle((nextTitle) => {
    layout.projectTitleButton.textContent = nextTitle;
  });
  layout.projectTitleButton.textContent = studioStore.getProjectTitle();
  layout.projectTitleButton.addEventListener("click", () => {
    const nextTitle = window.prompt(
      "Rename project",
      studioStore.getProjectTitle(),
    );
    if (nextTitle && nextTitle.trim().length > 0) {
      studioStore.setProjectTitle(nextTitle.trim());
    }
  });

  // Top-level Script access (_GLOBAL.md G5): open the living screenplay
  // column from the top control, available from any panel.
  layout.scriptAccessButton.addEventListener("click", () => {
    panelManager.open("script_editor");
  });

  // Mount the shared entity edit-drawer host once (_GLOBAL.md G6). Panels
  // open it via `openEntityDrawer(...)` from "../shared" — clicking any
  // inline entity edits it in place without navigating away.
  mountSharedEditDrawer(document.body);

  // Register every panel with the manager. (The script editor is NOT a
  // panel here — it lives in the persistent script column, wired via the
  // external opener above.)
  panelManager.register(
    "script_upload",
    new ScriptUploadPanel({ store: studioStore, panelManager }),
  );
  panelManager.register(
    "timeline",
    new TimelinePanel({ store: studioStore, panelManager }),
  );
  panelManager.register(
    "characters",
    new CharacterCreatorPanel({ store: studioStore }),
  );
  panelManager.register(
    "assets",
    new AssetsPanel({ store: studioStore }),
  );
  panelManager.register(
    "moodboard",
    new MoodBoardPanel({ store: studioStore }),
  );
  panelManager.register(
    "costume",
    new CostumePanel({ store: studioStore }),
  );
  panelManager.register(
    "location",
    new LocationPanel({ store: studioStore }),
  );
  panelManager.register(
    "storyboard",
    new StoryboardPanel({ store: studioStore, panelManager }),
  );
  panelManager.register(
    "final_video",
    new FinalVideoPanel({ store: studioStore }),
  );

  // Land on the first creative tool by default (the rail's top item).
  panelManager.open("location");

  // Grow the procedural side-margin trees behind the content. The field
  // renders independently of the widget (decorative) and also exposes
  // branch tips as perch targets, wired into Koko below. A `branchSeed`
  // on the inline config (or `?branchSeed=`) makes the layout
  // deterministic for tests.
  const branchField = new BranchField(document.body, { seed: resolveBranchSeed() });
  // Debug/test hook: lets specs read the live perch targets.
  window.__kokoBranchField = branchField;

  // Wire Koko's extension surface to the studio bridge. The widget
  // element exposes `setExtensions` (added in this prototype) which
  // forwards into KokoApp once it's online.
  const kokoElement = document.getElementById("koko") as KokoElement | null;
  if (!kokoElement) {
    console.error("[studio] <koko-companion> not found — extensions not wired.");
    return;
  }
  syncFooterHint(layout.footerHint, getKokoInteractionMode());
  // The action agent lives inside the widget's KokoApp; we have to
  // wait until KokoApp boots up to grab it. `connectedCallback` runs
  // synchronously when the element is in the DOM (which it already
  // is at this point), so the agent is available now. If we ever
  // race the upgrade, the bridge tolerates a null agent and falls
  // back to direct store mutation.
  // Cross-panel events dispatched by the rebuilt panels — wired here
  // where both the PanelManager and the widget element are available.
  // The Script Editor's character circles open the Character panel;
  // Final Video's premiere moment makes Koko cheer.
  window.addEventListener("koko:open-character", () => {
    panelManager.open("characters");
  });
  window.addEventListener("koko:premiere", () => {
    kokoElement.flashPill("🎬 That's a wrap — beautiful work!");
  });

  const actionAgent = kokoElement.getActionAgent();
  const bridge = new StudioKokoBridge({
    store: studioStore,
    panelManager,
    actionAgent,
    // After every choreographed turn drains, hand the list of
    // completed actions to KokoApp, which fires a follow-up Claude
    // call so the bird "talks back" in the response bubble + via
    // TTS. No tools, no screenshot — just dialogue.
    onTurnComplete: (actionsCompleted) => {
      void kokoElement.reportBack(actionsCompleted);
    },
  });
  kokoElement.setExtensions({
    ...bridge.buildExtensions(),
    // Perch on a real branch tip when idle; falls back to corners
    // automatically when the field is hidden (narrow viewport).
    getPerchPoints: () => branchField.getPerchPoints(),
  });
}

/**
 * Pin the procedural tree layout for reproducibility. Prefers a
 * `?branchSeed=` query param (tests use this — the inline config block
 * in studio.html overwrites the global, so a param survives), then
 * `window.__KOKO_CONFIG__.branchSeed`, else a fresh tree each load.
 */
function resolveBranchSeed(): number | undefined {
  const paramSeed = new URLSearchParams(window.location.search).get("branchSeed");
  if (paramSeed !== null && paramSeed.trim() !== "" && Number.isFinite(Number(paramSeed))) {
    return Number(paramSeed);
  }
  if (typeof window.__KOKO_CONFIG__?.branchSeed === "number") {
    return window.__KOKO_CONFIG__.branchSeed;
  }
  return undefined;
}

declare global {
  interface Window {
    __kokoBranchField?: BranchField;
    __studioPanelManager?: PanelManager;
    __studioStore?: StudioStore;
  }
}

interface PageChrome {
  topStrip: HTMLElement;
  headerHost: HTMLElement;
  panelHost: HTMLElement;
  footerHint: HTMLElement;
  projectTitleButton: HTMLButtonElement;
  scriptAccessButton: HTMLButtonElement;
  themeToggleButton: HTMLButtonElement;
  backgroundPicker: HTMLDivElement;
}

function buildPageChrome(initialTheme: StudioTheme): PageChrome {
  const topStrip = document.createElement("nav");
  topStrip.className = "studio-top-strip";

  const brand = document.createElement("span");
  brand.className = "studio-brand";
  brand.textContent = "koyal studio";

  const projectTitleButton = document.createElement("button");
  projectTitleButton.type = "button";
  projectTitleButton.className = "studio-project-title";
  projectTitleButton.title = "Click to rename";
  projectTitleButton.textContent = "Untitled film";
  projectTitleButton.dataset.kokoAction = "project-title";

  // Top-level SCRIPT access (_GLOBAL.md G5): the living screenplay is
  // reachable any time from a primary top control, not a sidebar toggle.
  const scriptAccessButton = document.createElement("button");
  scriptAccessButton.type = "button";
  scriptAccessButton.className = "studio-script-access";
  scriptAccessButton.textContent = "Script";
  scriptAccessButton.title = "Open the screenplay";
  scriptAccessButton.dataset.kokoAction = "open-script";

  const hint = document.createElement("span");
  hint.className = "studio-hint";
  hint.textContent = "Speak with Ctrl+`";

  const themeToggleButton = document.createElement("button");
  themeToggleButton.type = "button";
  themeToggleButton.className = "studio-theme-toggle";
  syncThemeToggleLabel(themeToggleButton, initialTheme);

  const backgroundPicker = buildBackgroundPicker();

  // NOTE (_GLOBAL.md / sidebar.md): the Action/Suggestion mode toggle and
  // "Show Koko" controls were REMOVED from chrome — they live in Koko
  // settings now (koko-bird.md). The mode mechanism itself stays.

  topStrip.appendChild(brand);
  topStrip.appendChild(projectTitleButton);
  topStrip.appendChild(scriptAccessButton);
  topStrip.appendChild(backgroundPicker);
  topStrip.appendChild(themeToggleButton);
  topStrip.appendChild(hint);

  const headerHost = document.createElement("div");
  // Header content gets injected per-panel via PanelManager subscribe.

  const panelHost = document.createElement("section");
  panelHost.className = "studio-panel-host";

  const footerHint = document.createElement("div");
  footerHint.className = "studio-footer-hint";
  syncFooterHint(footerHint, getKokoInteractionMode());

  return {
    topStrip,
    headerHost,
    panelHost,
    footerHint,
    projectTitleButton,
    scriptAccessButton,
    themeToggleButton,
    backgroundPicker,
  };
}

/**
 * Background scene picker: one swatch button per option (a "Paper"
 * no-image default plus the watercolor scenes). Lives in the top strip
 * next to the theme toggle; selection applies instantly and persists.
 */
function buildBackgroundPicker(): HTMLDivElement {
  const picker = document.createElement("div");
  picker.className = "studio-bg-picker";
  picker.setAttribute("role", "radiogroup");
  picker.setAttribute("aria-label", "Background scene");

  for (const option of BACKGROUND_OPTIONS) {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.className = "studio-bg-swatch";
    swatch.dataset.bgOption = option.id;
    swatch.title = option.label;
    swatch.setAttribute("role", "radio");
    swatch.setAttribute("aria-label", option.label);
    if (option.thumb) {
      swatch.style.backgroundImage = `url("${option.thumb}")`;
    } else {
      swatch.classList.add("studio-bg-swatch-paper");
    }
    picker.appendChild(swatch);
  }
  return picker;
}

function wireBackgroundPicker(
  picker: HTMLDivElement,
  initial: StudioBackground,
): void {
  const syncSelected = (active: StudioBackground): void => {
    for (const swatch of picker.querySelectorAll<HTMLButtonElement>(".studio-bg-swatch")) {
      const isActive = swatch.dataset.bgOption === active;
      swatch.classList.toggle("selected", isActive);
      swatch.setAttribute("aria-checked", isActive ? "true" : "false");
    }
  };
  syncSelected(initial);
  picker.addEventListener("click", (event) => {
    const swatch = (event.target as HTMLElement).closest<HTMLButtonElement>(
      ".studio-bg-swatch",
    );
    if (!swatch) return;
    const next = swatch.dataset.bgOption as StudioBackground | undefined;
    if (!next) return;
    applyBackground(next);
    syncSelected(next);
  });
}

/** Insert the fixed scene layer behind the shell (once). */
function installBackgroundLayer(): void {
  if (document.querySelector(".studio-bg-layer")) return;
  const layer = document.createElement("div");
  layer.className = "studio-bg-layer";
  layer.setAttribute("aria-hidden", "true");
  document.body.prepend(layer);
}

function readPreferredBackground(): StudioBackground {
  const saved =
    typeof window !== "undefined"
      ? window.localStorage.getItem(BACKGROUND_STORAGE_KEY)
      : null;
  if (BACKGROUND_OPTIONS.some((option) => option.id === saved)) {
    return saved as StudioBackground;
  }
  return "paper";
}

function applyBackground(background: StudioBackground): void {
  document.documentElement.dataset.bg = background;
  window.localStorage.setItem(BACKGROUND_STORAGE_KEY, background);
}

function buildHeader(activePanelId: StudioPanelId): HTMLElement {
  const header = document.createElement("header");
  header.className = "studio-header";

  const title = document.createElement("h2");
  title.className = "studio-header-title";
  title.textContent = PANEL_REGISTRY[activePanelId].title;
  header.appendChild(title);

  return header;
}

function readPreferredTheme(): StudioTheme {
  const saved =
    typeof window !== "undefined" ? window.localStorage.getItem(THEME_STORAGE_KEY) : null;
  if (saved === "light" || saved === "dark") return saved;
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function getActiveTheme(): StudioTheme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

function applyTheme(theme: StudioTheme): void {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  window.localStorage.setItem(THEME_STORAGE_KEY, theme);
}

function syncThemeToggleLabel(button: HTMLButtonElement, theme: StudioTheme): void {
  button.textContent = theme === "dark" ? "Light mode" : "Dark mode";
  button.setAttribute("aria-label", `Switch to ${theme === "dark" ? "light" : "dark"} mode`);
}

function syncFooterHint(el: HTMLElement, mode: KokoInteractionMode): void {
  el.textContent =
    mode === "action"
      ? "Tell Koko what to make. Press / to type."
      : "Suggest mode — Koko drafts in forms; you click submit. Press / to type.";
}

// Sibling-blur on hover (and other hover transitions) feel glitchy when
// the page loads with the cursor already parked over a row — the hover
// state fires on first paint with no easing, and a single jittery frame
// can trip the blur cascade. The `hover-cold` class disables transitions
// + the blur effect until the user actually moves the pointer 12px or
// performs a real click. Mirrors the technique used by ja.mt.
function installHoverColdGuard(): void {
  const html = document.documentElement;
  html.classList.add("hover-cold");
  let firstX = -1;
  let firstY = -1;
  const clearCold = (): void => {
    html.classList.remove("hover-cold");
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerdown", clearCold);
  };
  const onMove = (event: PointerEvent): void => {
    if (firstX < 0) {
      firstX = event.clientX;
      firstY = event.clientY;
      return;
    }
    const dx = event.clientX - firstX;
    const dy = event.clientY - firstY;
    if (dx * dx + dy * dy > 144) clearCold();
  };
  window.addEventListener("pointermove", onMove, { passive: true });
  window.addEventListener("pointerdown", clearCold, { passive: true });
}
